/**
 * Which build of the dashboard a page is running, and which build is deployed.
 *
 * Shared by the two ends of one question and by nothing else. `vite.config.ts` stamps an id into
 * every build — into `index.html`, where a page reads it back, and into `/version.json`, which is
 * what a tab that has been open all day fetches — and `update.ts` compares the two. The config
 * imports this file by its real filename, like `oauth-client.ts`, and it touches no Node API for the
 * same reason that one does not: the browser loads half of it.
 *
 * ## The id is the bytes, not the commit
 *
 * Every push to `main` redeploys the dashboard, including the pushes that only touched another
 * workspace or the API, and most of those change nothing a browser downloads. An id minted per build or
 * per commit would tell every open tab to reload after each of them, for nothing — and a notice that
 * is usually wrong is a notice people learn to leave on the screen. So the id is a digest of what the
 * build serves: two builds with identical output have the same id, and a change to any byte a tab
 * would load is a different one. The commit rides along in `version.json` for whoever is reading it
 * with `curl`; nothing compares it.
 */

/** Where the deployed build names itself. A real file in `dist/`, never the SPA fallback. */
export const VERSION_PATH = '/version.json';

/** The `<meta name>` that carries the id of the build a page was loaded from. */
export const BUILD_META = 'boardflight-build';

export interface VersionDocument {
  build: string;
  /** The commit the build was cut from, when the build could tell. Informational only. */
  commit: string | null;
}

/** One file the build emits, as the digest reads it. */
export interface BuiltFile {
  fileName: string;
  content: string | Uint8Array;
}

/**
 * The id of a build: sixteen hex digits of a SHA-256 over the page and every file beside it.
 *
 * Source maps are left out — no tab ever loads one, and the chunk each one describes is already in
 * the digest. Every file goes in as its name, its length and then its bytes, so two different sets
 * of files can never frame into the same stream; and they go in sorted by name, so the order a
 * bundler happens to list them in cannot move the id.
 */
export async function buildIdOf(html: string, files: readonly BuiltFile[]): Promise<string> {
  const encoder = new TextEncoder();
  const parts: Uint8Array[] = [];
  const add = (label: string, bytes: Uint8Array) => {
    parts.push(encoder.encode(`${label}\n${bytes.byteLength}\n`), bytes);
  };

  add('index.html', encoder.encode(html));
  const served = files
    .filter((file) => !file.fileName.endsWith('.map'))
    .sort((a, b) => (a.fileName < b.fileName ? -1 : a.fileName > b.fileName ? 1 : 0));
  for (const file of served) {
    add(file.fileName, typeof file.content === 'string' ? encoder.encode(file.content) : file.content);
  }

  const stream = new Uint8Array(parts.reduce((total, part) => total + part.byteLength, 0));
  let offset = 0;
  for (const part of parts) {
    stream.set(part, offset);
    offset += part.byteLength;
  }
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', stream));
  return Array.from(digest.subarray(0, 8), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** The body of `version.json`. */
export function versionDocument(build: string, commit: string | null): string {
  const document: VersionDocument = { build, commit };
  return `${JSON.stringify(document, null, 2)}\n`;
}

/**
 * A `version.json`, or `null` for anything that is not one.
 *
 * Tolerant in the direction that matters, because of who reads it: a tab loaded from an *older*
 * build than the file. So a field added later is ignored rather than refused, and the id is not held
 * to today's sixteen hex digits — a tab that rejected a longer id from some future build would never
 * be told about that build, which is the one failure this whole mechanism exists to prevent. What it
 * will not accept is a document without an id at all, which includes the dashboard's own
 * `index.html`: what a deployment that has lost the file answers with.
 */
export function readVersion(body: unknown): VersionDocument | null {
  if (typeof body !== 'object' || body === null) return null;
  const { build, commit } = body as Record<string, unknown>;
  if (typeof build !== 'string' || build.length === 0 || build.length > 128) return null;
  return { build, commit: typeof commit === 'string' && commit !== '' ? commit : null };
}
